import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, BriefcaseBusiness, Calendar, ChevronDown, ChevronUp, ExternalLink, Eye, Pencil, Plus, Trash2 } from "lucide-react";

import PilotageEmptyState from "@/components/pilotage/PilotageEmptyState";
import PilotageField from "@/components/pilotage/PilotageField";
import PilotageModal from "@/components/pilotage/PilotageModal";
import PilotageTable from "@/components/pilotage/PilotageTable";
import AppErrorState from "@/components/ui/AppErrorState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage, extractFieldValidationErrors } from "@/lib/api";
import {
  DEFAULT_INTERMEDIAIRE_MARGIN_PCT,
  formatPct,
  resolveIntermediairePct,
} from "@/lib/businessRules";
import {
  MISSION_STATUSES,
  createMission,
  deleteMission,
  listClients,
  listCollaborators,
  listMissions,
  updateMission,
} from "@/lib/pilotageApi";
import {
  DEFAULT_EMPLOYER_COST_COEFFICIENT,
  CONSULTANT_RATE_LABEL,
  TACE_LABEL,
  INTERMEDIARY_MARGIN_LABEL,
  MISSION_TYPES,
  buildMissionPayload,
  formatDateLabel,
  formatEUR0,
  formatPercentInputValue,
  normalizeMissionFormForType,
  resolveCollaboratorContractType,
  resolveMissionTypeWithCollaborator,
  resolveEmployerCostCoefficient,
  parseLocaleNumber,
  parsePercentInputToRatio,
} from "@/lib/pilotageUi";

const MISSION_STATUS_LABELS = {
  ACTIVE: "Active",
  PLANNED: "Planifiée",
  ENDED: "Terminée",
};

const PERIOD_FILTERS = [
  { value: "all", label: "Toutes" },
  { value: "current", label: "En cours" },
  { value: "future", label: "À venir" },
  { value: "ended", label: "Terminées" },
  { value: "no_end", label: "Sans date de fin" },
];

const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const todayAtMidnight = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const resolveMissionPeriod = (mission, today = todayAtMidnight()) => {
  const startDate = parseDateValue(mission?.start_date);
  const endDate = parseDateValue(mission?.end_date);

  if (startDate && startDate > today) {
    return "future";
  }
  if (endDate && endDate < today) {
    return "ended";
  }
  if (startDate && startDate <= today && (!endDate || endDate >= today)) {
    return "current";
  }
  return "other";
};

const missionSortRank = (mission) => {
  const period = resolveMissionPeriod(mission);
  if (period === "current") {
    return 0;
  }
  if (period === "future") {
    return 1;
  }
  if (period === "ended") {
    return 2;
  }
  return 3;
};

const uniqueOptions = (items, getValue, getLabel) =>
  items
    .map((item) => ({
      value: String(getValue(item) || ""),
      label: String(getLabel(item) || getValue(item) || ""),
    }))
    .filter((item) => item.value && item.label)
    .filter((item, index, array) =>
      array.findIndex((candidate) => candidate.value === item.value) === index,
    )
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));

const toInputDate = (value) => {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
};

const EMPTY_FORM = {
  type: "FREELANCE",
  collaborator_id: "",
  client_id: "",
  client_contact_id: "",
  mission_role: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  status: "ACTIVE",
  tarif_client: "",
  tjm_consultant: "",
  salaire_annuel: "",
  employer_cost_coefficient: "1.50",
  tace: "80",
  intermediaire_margin_mode: "PERCENT",
  intermediaire_margin_pct: formatPercentInputValue(DEFAULT_INTERMEDIAIRE_MARGIN_PCT),
  intermediaire_margin_amount: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  client_free_days_granted: "",
  rate_changes: [],
  expected_collab_salary_snapshot: null,
};

const getRateFieldLabel = (field) => {
  switch (field) {
    case "tjm":
      return "TJM client";
    case "cjm":
      return "CJM consultant";
    case "annual_salary":
    case "salary":
      return "Salaire annuel CDI";
    default:
      return field;
  }
};

const formatDateFr = (isoDate) => {
  if (!isoDate) return "";
  const parts = String(isoDate).slice(0, 10).split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return String(isoDate);
};

const computeEvolutionDiff = (rc, form) => {
  const field = rc.field === "salary" ? "annual_salary" : rc.field;
  let baseVal = 0;
  if (field === "tjm") {
    baseVal = parseLocaleNumber(form.tarif_client) || 0;
  } else if (field === "cjm") {
    baseVal = parseLocaleNumber(form.tjm_consultant) || 0;
  } else if (field === "annual_salary") {
    baseVal = parseLocaleNumber(form.salaire_annuel) || 0;
  }

  const priorChanges = (form.rate_changes || [])
    .filter(
      (other) =>
        other.id !== rc.id &&
        (other.field === field || (field === "annual_salary" && other.field === "salary")),
    )
    .filter((other) => other.effective_date < rc.effective_date)
    .sort((a, b) => a.effective_date.localeCompare(b.effective_date));

  const getRcVal = (item) => (item?.value !== undefined && item?.value !== null ? Number(item.value) : Number(item?.new_value));
  const prevValue =
    priorChanges.length > 0
      ? getRcVal(priorChanges[priorChanges.length - 1])
      : baseVal;
  const newValue = getRcVal(rc) || 0;
  const diffAmount = newValue - prevValue;
  const diffPct = prevValue > 0 ? (diffAmount / prevValue) * 100 : 0;

  return { prevValue, newValue, diffAmount, diffPct };
};

const buildFormValuesForCollaborator = (formValues, collaborator) => {
  const collaboratorType = resolveCollaboratorContractType(collaborator);

  if (!collaboratorType) {
    return formValues;
  }

  const nextForm = normalizeMissionFormForType(formValues, collaboratorType);
  if (["CDI", "CDD", "ALTERNANT"].includes(collaboratorType) && collaborator?.annual_salary != null) {
    nextForm.salaire_annuel = String(collaborator.annual_salary);
    if (collaborator.employer_cost_coefficient != null) {
      nextForm.employer_cost_coefficient = String(collaborator.employer_cost_coefficient);
    }
  }
  return nextForm;
};

const MissionsPage = () => {
  const { role } = useAuth();
  const isReadOnly = role === "RH";
  const [searchParams, setSearchParams] = useSearchParams();

  const [missions, setMissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [rateChangesExpanded, setRateChangesExpanded] = useState(false);
  const [rateChangeForm, setRateChangeForm] = useState(null);
  const [rateChangeSubformError, setRateChangeSubformError] = useState("");
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    clientId: "all",
    collaboratorId: "all",
    period: "all",
  });
  const collaboratorFilter = searchParams.get("collaborator") || "";
  const showEmptyState = !loading && missions.length === 0;

  const collaboratorName = (collaboratorId) =>
    collaborators.find((item) => item.id === collaboratorId)?.full_name || collaboratorId;

  const collaboratorById = (collaboratorId) =>
    collaborators.find((item) => item.id === collaboratorId);

  const clientName = (clientId) =>
    clients.find((item) => item.id === clientId)?.name || clientId;
  const filteredCollaborator = collaboratorById(collaboratorFilter);

  const missionTypeForDisplay = (mission) =>
    resolveMissionTypeWithCollaborator(
      mission,
      collaboratorById(mission.collaborator_id),
    );

  const clientNamesById = useMemo(
    () => new Map(clients.map((client) => [client.id, client.name])),
    [clients],
  );

  const collaboratorsById = useMemo(
    () => new Map(collaborators.map((collaborator) => [collaborator.id, collaborator])),
    [collaborators],
  );

  const missionStatusLabel = (status) => MISSION_STATUS_LABELS[status] || status || "—";

  const statusOptions = useMemo(() => {
    const statuses = [
      ...MISSION_STATUSES,
      ...missions.map((mission) => mission?.status).filter(Boolean),
    ];

    return Array.from(new Set(statuses)).map((status) => ({
      value: status,
      label: missionStatusLabel(status),
    }));
  }, [missions]);

  const clientOptions = useMemo(
    () => uniqueOptions(clients, (client) => client.id, (client) => client.name),
    [clients],
  );

  const collaboratorOptions = useMemo(
    () =>
      uniqueOptions(
        collaborators,
        (collaborator) => collaborator.id,
        (collaborator) => collaborator.full_name,
      ),
    [collaborators],
  );

  const filteredMissions = useMemo(() => {
    const query = normalizeSearchText(filters.search);

    return missions
      .filter((mission) => {
        if (
          collaboratorFilter &&
          String(mission?.collaborator_id || "") !== collaboratorFilter
        ) {
          return false;
        }
        if (filters.status !== "all" && mission.status !== filters.status) {
          return false;
        }
        if (filters.clientId !== "all" && mission.client_id !== filters.clientId) {
          return false;
        }
        if (
          filters.collaboratorId !== "all" &&
          mission.collaborator_id !== filters.collaboratorId
        ) {
          return false;
        }
        if (filters.period === "no_end" && mission.end_date) {
          return false;
        }
        if (
          filters.period !== "all" &&
          filters.period !== "no_end" &&
          resolveMissionPeriod(mission) !== filters.period
        ) {
          return false;
        }
        if (!query) {
          return true;
        }

        const searchValues = [
          mission.mission_role,
          mission.client_name,
          clientNamesById.get(mission.client_id) || mission.client_id,
          collaboratorsById.get(mission.collaborator_id)?.full_name || mission.collaborator_id,
          mission.client_contact_name,
          mission.status,
          missionStatusLabel(mission.status),
          resolveMissionTypeWithCollaborator(
            mission,
            collaboratorsById.get(mission.collaborator_id),
          ),
          mission.address,
          mission.start_date,
          mission.end_date,
        ];

        return normalizeSearchText(searchValues.join(" ")).includes(query);
      })
      .slice()
      .sort((a, b) => {
        const rankDiff = missionSortRank(a) - missionSortRank(b);
        if (rankDiff !== 0) {
          return rankDiff;
        }
        return String(a.start_date || "").localeCompare(String(b.start_date || ""));
      });
  }, [missions, filters, collaboratorFilter, clientNamesById, collaboratorsById]);

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "all",
      clientId: "all",
      collaboratorId: "all",
      period: "all",
    });

    if (collaboratorFilter) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete("collaborator");
      setSearchParams(nextSearchParams, { replace: true });
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError("");

    try {
      const [missionsData, clientsData, collaboratorsData] = await Promise.all([
        listMissions(),
        listClients(),
        listCollaborators(),
      ]);

      setMissions(missionsData);
      setClients(clientsData);
      setCollaborators(collaboratorsData);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible de charger les missions."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const openCreateModal = () => {
    const defaultCollaborator = collaborators[0];
    setEditingMission(null);
    setFormError("");
    setFieldErrors({});
    setRateChangesExpanded(false);
    setRateChangeForm(null);
    setRateChangeSubformError("");
    setFormValues(buildFormValuesForCollaborator({
      ...EMPTY_FORM,
      rate_changes: [],
      collaborator_id: defaultCollaborator?.id || "",
      client_id: clients[0]?.id || "",
    }, defaultCollaborator));
    setModalOpen(true);
  };

  const openEditModal = (mission) => {
    const linkedCollaborator = collaboratorById(mission.collaborator_id);
    const missionType = resolveMissionTypeWithCollaborator(mission, linkedCollaborator, {
      logWarnings: true,
    });
    setEditingMission(mission);
    setFormError("");
    setFieldErrors({});
    setRateChangesExpanded(false);
    setRateChangeForm(null);
    setRateChangeSubformError("");
    const marginMode = mission.intermediaire_margin_mode || "PERCENT";
    const marginPct = resolveIntermediairePct(mission);
    const marginAmt = mission.intermediaire_margin_amount !== undefined && mission.intermediaire_margin_amount !== null
      ? mission.intermediaire_margin_amount
      : (parseLocaleNumber(mission.tarif_client) || 0) * marginPct;

    const normalizedRateChanges = Array.isArray(mission.rate_changes)
      ? mission.rate_changes.map((rc) => {
          const val = rc.value !== undefined && rc.value !== null ? rc.value : rc.new_value;
          return {
            ...rc,
            value: val,
            new_value: val,
          };
        })
      : [];

    setFormValues(normalizeMissionFormForType({
      type: missionType,
      collaborator_id: mission.collaborator_id || "",
      client_id: mission.client_id || "",
      client_contact_id: mission.client_contact_id || "",
      mission_role: mission.mission_role || "",
      start_date: toInputDate(mission.start_date),
      end_date: toInputDate(mission.end_date),
      status: mission.status || "ACTIVE",
      tarif_client: String(mission.tarif_client ?? ""),
      tjm_consultant: mission.tjm_consultant === null ? "" : String(mission.tjm_consultant ?? ""),
      salaire_annuel: linkedCollaborator?.annual_salary != null
        ? String(linkedCollaborator.annual_salary)
        : (mission.salaire_annuel === null ? "" : String(mission.salaire_annuel ?? "")),
      employer_cost_coefficient: linkedCollaborator?.employer_cost_coefficient != null
        ? String(linkedCollaborator.employer_cost_coefficient)
        : (mission.employer_cost_coefficient != null ? String(mission.employer_cost_coefficient) : "1.50"),
      tace: formatPercentInputValue(mission.tace ?? 0.8),
      intermediaire_margin_mode: marginMode,
      intermediaire_margin_pct: formatPercentInputValue(marginPct),
      intermediaire_margin_amount: marginAmt !== null && marginAmt !== undefined ? String(marginAmt) : "",
      contact_email: mission.contact_email || "",
      contact_phone: mission.contact_phone || "",
      address: mission.address || "",
      client_free_days_granted: mission.client_free_days_granted != null ? String(mission.client_free_days_granted) : "0",
      rate_changes: normalizedRateChanges,
      expected_collab_salary_snapshot: linkedCollaborator?.annual_salary != null ? Number(linkedCollaborator.annual_salary) : null,
    }, missionType));
    setModalOpen(true);
  };

  const closeModal = (nextOpen) => {
    setModalOpen(nextOpen);
    if (!nextOpen) {
      setEditingMission(null);
      setFormError("");
      setFieldErrors({});
      setRateChangesExpanded(false);
      setRateChangeForm(null);
      setRateChangeSubformError("");
      setFormValues(EMPTY_FORM);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    setFormError("");
    setFieldErrors({});

    const submitCollaborator = collaboratorById(formValues.collaborator_id);
    const normalizedFormValues = buildFormValuesForCollaborator(formValues, submitCollaborator);
    const payload = buildMissionPayload(normalizedFormValues);
    payload.contact_email = formValues.contact_email.trim() || null;
    payload.contact_phone = formValues.contact_phone.trim() || null;
    payload.address = formValues.address.trim() || null;
    payload.client_contact_id = formValues.client_contact_id || null;
    if (editingMission && formValues.expected_collab_salary_snapshot != null) {
      payload.expected_collab_salary_snapshot = formValues.expected_collab_salary_snapshot;
    }

    const errors = {};
    if (formValues.tarif_client === "") {
      errors.tarif_client = "Le TJM client est obligatoire.";
    }
    const tarifNum = parseLocaleNumber(formValues.tarif_client) || 0;
    const mode = formValues.intermediaire_margin_mode || "PERCENT";
    if (mode === "AMOUNT") {
      const amtNum = parseLocaleNumber(formValues.intermediaire_margin_amount) || 0;
      if (amtNum > tarifNum) {
        errors.intermediaire_margin_amount = "La marge porteur en euros ne peut pas être supérieure au TJM client.";
      }
    } else {
      const pctRatio = parsePercentInputToRatio(formValues.intermediaire_margin_pct, 0);
      if (pctRatio > 1) {
        errors.intermediaire_margin_pct = "La marge porteur en pourcentage ne peut pas dépasser 100 %.";
      }
    }
    if (editingMission) {
      const grantedVal = parseLocaleNumber(formValues.client_free_days_granted) || 0;
      const consumedVal = editingMission.client_free_days_consumed || 0;
      if (grantedVal < consumedVal) {
        errors.client_free_days_granted = `Le nombre de jours gratuits accordés (${grantedVal} j) ne peut pas être inférieur aux jours déjà consommés (${consumedVal} j).`;
      }
    }
    if (
      formValues.start_date &&
      formValues.end_date &&
      formValues.start_date > formValues.end_date
    ) {
      errors.end_date = "La date de fin doit être postérieure ou égale à la date de début.";
    }
    if (payload.type === "CDI") {
      if (payload.salaire_annuel === null) {
        errors.salaire_annuel = "Le salaire annuel est obligatoire pour une mission CDI.";
      }
      const coeffVal = parseLocaleNumber(formValues.employer_cost_coefficient);
      if (coeffVal === null || isNaN(coeffVal) || coeffVal <= 0 || coeffVal > 10) {
        errors.employer_cost_coefficient = "Le coefficient de coût employeur doit être un nombre positif (ex: 1,50).";
      }
    }
    if (payload.type === "FREELANCE" && payload.tjm_consultant === null) {
      errors.tjm_consultant = `Le ${CONSULTANT_RATE_LABEL} est obligatoire pour une mission Freelance.`;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError(
        Object.keys(errors).length > 1
          ? "Certaines informations de la mission sont incorrectes. Vérifiez les champs signalés avant d'enregistrer."
          : Object.values(errors)[0],
      );
      setSaving(false);
      return;
    }

    try {
      if (editingMission) {
        await updateMission(editingMission.id, payload);
      } else {
        await createMission(payload);
      }

      closeModal(false);
      await loadData();
    } catch (requestError) {
      const extractedErrors = extractFieldValidationErrors(requestError);
      if (Object.keys(extractedErrors).length > 0) {
        setFieldErrors(extractedErrors);
        if (extractedErrors.rate_changes) {
          setRateChangesExpanded(true);
        }
      }
      setFormError(
        getApiErrorMessage(requestError, "Impossible d'enregistrer la mission pour le moment. Veuillez réessayer."),
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedCollaborator = collaboratorById(formValues.collaborator_id);
  const inheritedMissionType = resolveCollaboratorContractType(selectedCollaborator);
  const isMissionTypeLocked = Boolean(inheritedMissionType);
  const effectiveMissionType = inheritedMissionType || formValues.type;

  const calculateDisplayFinancials = () => {
    const tarif = parseLocaleNumber(formValues.tarif_client) || 0;
    const mode = formValues.intermediaire_margin_mode || "PERCENT";

    let pctRatio;
    let amtValue;

    if (mode === "AMOUNT") {
      amtValue = parseLocaleNumber(formValues.intermediaire_margin_amount) || 0;
      pctRatio = tarif > 0 ? amtValue / tarif : 0;
    } else {
      pctRatio = parsePercentInputToRatio(formValues.intermediaire_margin_pct) || 0;
      amtValue = tarif * pctRatio;
    }

    const tjmNet = tarif - amtValue;
    return {
      tarif,
      mode,
      pctDisplay: `${(pctRatio * 100).toFixed(2).replace(".", ",")} %`,
      amtDisplay: `${amtValue.toFixed(2).replace(".", ",")} €/jour`,
      tjmNetDisplay: `${tjmNet.toFixed(2).replace(".", ",")} €/jour`,
    };
  };

  const displayFinancials = calculateDisplayFinancials();

  const cdiCostEstimates = useMemo(() => {
    const salary = parseLocaleNumber(formValues.salaire_annuel) || 0;
    const coeff = parseLocaleNumber(formValues.employer_cost_coefficient);
    const validCoeff =
      coeff !== null && !isNaN(coeff) && coeff > 0
        ? coeff
        : DEFAULT_EMPLOYER_COST_COEFFICIENT;
    const annualLoadedCost = salary * validCoeff;
    const dailyCost = annualLoadedCost / 210;
    return {
      annualLoadedCost,
      dailyCost,
    };
  }, [formValues.salaire_annuel, formValues.employer_cost_coefficient]);

  const handleStartAddRateChange = () => {
    setRateChangeSubformError("");
    setRateChangeForm({
      id: null,
      effective_date: "",
      field: "tjm",
      value: "",
      new_value: "",
      comment: "",
      isPastDate: false,
    });
  };

  const handleStartEditRateChange = (rc) => {
    if (["annual_salary", "salary"].includes(rc.field)) return;
    setRateChangeSubformError("");
    const isPast = rc.effective_date <= todayIso;
    const rcVal = rc.value !== undefined && rc.value !== null ? rc.value : rc.new_value;
    setRateChangeForm({
      id: rc.id || `${rc.field}-${rc.effective_date}`,
      effective_date: toInputDate(rc.effective_date),
      field: rc.field === "salary" ? "annual_salary" : rc.field,
      value: String(rcVal ?? ""),
      new_value: String(rcVal ?? ""),
      comment: rc.comment || "",
      isPastDate: isPast,
    });
  };

  const handleDeleteRateChange = (rc) => {
    if (["annual_salary", "salary"].includes(rc.field)) return;
    if (window.confirm("Supprimer cette évolution tarifaire ?")) {
      setFormValues((curr) => ({
        ...curr,
        rate_changes: (curr.rate_changes || []).filter(
          (item) =>
            item.id
              ? item.id !== rc.id
              : !(item.field === rc.field && item.effective_date === rc.effective_date),
        ),
      }));
    }
  };

  const handleSaveRateChange = () => {
    if (!rateChangeForm.effective_date) {
      setRateChangeSubformError("Sélectionnez la date à partir de laquelle le nouveau tarif doit s'appliquer.");
      return;
    }
    const rawVal = rateChangeForm.value !== undefined && rateChangeForm.value !== ""
      ? rateChangeForm.value
      : rateChangeForm.new_value;
    if (rawVal === undefined || rawVal === null || String(rawVal).trim() === "") {
      setRateChangeSubformError("Renseignez le nouveau montant du TJM.");
      return;
    }
    const numVal = parseLocaleNumber(rawVal);
    if (numVal === null || isNaN(numVal) || numVal < 0) {
      setRateChangeSubformError("Le montant saisi doit être un nombre valide supérieur ou égal à zéro, selon les règles de la mission.");
      return;
    }
    if (formValues.start_date && rateChangeForm.effective_date < formValues.start_date) {
      setRateChangeSubformError("Cette date est en dehors de la période de la mission. Vérifiez les dates saisies.");
      return;
    }
    if (formValues.end_date && rateChangeForm.effective_date > formValues.end_date) {
      setRateChangeSubformError("Cette date est en dehors de la période de la mission. Vérifiez les dates saisies.");
      return;
    }

    const duplicate = (formValues.rate_changes || []).find((item) => {
      const isSameItem =
        rateChangeForm.id &&
        (item.id === rateChangeForm.id ||
          (item.field === rateChangeForm.field && item.effective_date === rateChangeForm.effective_date));
      if (isSameItem) return false;
      return item.field === rateChangeForm.field && item.effective_date === rateChangeForm.effective_date;
    });

    if (duplicate) {
      setRateChangeSubformError("Une évolution de TJM existe déjà à cette date. Modifiez-la ou choisissez une autre date.");
      return;
    }

    const newEntry = {
      id: rateChangeForm.id || `rc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      effective_date: rateChangeForm.effective_date,
      field: rateChangeForm.field,
      value: numVal,
      new_value: numVal,
      comment: rateChangeForm.comment?.trim() || null,
    };

    setFormValues((curr) => {
      const existing = curr.rate_changes || [];
      const filtered = rateChangeForm.id
        ? existing.filter(
            (item) =>
              item.id !== rateChangeForm.id &&
              !(item.field === rateChangeForm.field && item.effective_date === rateChangeForm.effective_date),
          )
        : existing;
      const nextList = [...filtered, newEntry].sort((a, b) =>
        a.effective_date.localeCompare(b.effective_date),
      );
      return {
        ...curr,
        rate_changes: nextList,
      };
    });

    setRateChangeForm(null);
    setRateChangeSubformError("");
  };

  const rateChangeLiveComparison = useMemo(() => {
    if (!rateChangeForm) return null;
    const rawVal = rateChangeForm.value !== undefined && rateChangeForm.value !== ""
      ? rateChangeForm.value
      : rateChangeForm.new_value;
    if (!rawVal) return null;
    const numVal = parseLocaleNumber(rawVal);
    if (numVal === null || isNaN(numVal) || numVal < 0) return null;
    return computeEvolutionDiff(
      {
        id: rateChangeForm.id,
        field: rateChangeForm.field,
        effective_date: rateChangeForm.effective_date || "9999-12-31",
        value: numVal,
        new_value: numVal,
      },
      formValues,
    );
  }, [rateChangeForm, formValues]);

  const handleDelete = async (mission) => {
    const shouldDelete = window.confirm(
      `Supprimer la mission ${mission.id} ?`,
    );

    if (!shouldDelete) {
      return;
    }

    setError("");

    try {
      await deleteMission(mission.id);
      await loadData();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible de supprimer la mission."));
    }
  };

  const columns = [
    {
      key: "mission",
      label: "Mission",
      render: (mission) => {
        const missionType = missionTypeForDisplay(mission);

        return (
          <div>
            <p className="font-medium text-white">{clientName(mission.client_id)}</p>
            {mission.mission_role ? (
              <p className="mt-1 text-sm font-semibold text-lenvia-blue">{mission.mission_role}</p>
            ) : null}
            <p className="mt-1 text-xs text-slate-300">
              Contact client: {mission.client_contact_name || "—"}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {collaboratorName(mission.collaborator_id)}
            </p>
            <p className="mt-1 text-xs text-slate-500">Type: {missionType}</p>
          </div>
        );
      },
    },
    {
      key: "period",
      label: "Période",
      render: (mission) => (
        <div>
          <p>{formatDateLabel(mission.start_date)}</p>
          <p className="mt-1 text-xs text-slate-500">
            {mission.end_date ? `au ${formatDateLabel(mission.end_date)}` : "Sans fin prévue"}
          </p>
        </div>
      ),
    },
    {
      key: "status",
      label: "Statut",
      render: (mission) => missionStatusLabel(mission.status),
    },
  ];

  if (!isReadOnly) {
    columns.push({
      key: "finance",
      label: "Pilotage financier",
      render: (mission) => {
        const missionType = missionTypeForDisplay(mission);

        return (
          <div>
            <p>TJM client: {formatEUR0(mission.tarif_client)}</p>
            <p className="mt-1 text-xs text-slate-500">
              {missionType === "FREELANCE"
                ? `${CONSULTANT_RATE_LABEL}: ${formatEUR0(mission.tjm_consultant)}`
                : `Salaire annuel: ${formatEUR0(mission.salaire_annuel)} (coeff. ${resolveEmployerCostCoefficient(mission).toFixed(2).replace(".", ",")})`}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {INTERMEDIARY_MARGIN_LABEL}: {formatPct(resolveIntermediairePct(mission))}
            </p>
            {mission.client_free_days_granted > 0 ? (
              <p className="mt-1 text-xs text-amber-300">
                Gratuité : {mission.client_free_days_consumed || 0} / {mission.client_free_days_granted} j
              </p>
            ) : null}
          </div>
        );
      },
    });
  }

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Pilotage
            </p>
            <h1 className="mt-2 text-2xl font-heading font-bold text-white">
              Missions
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Vue centralisée des placements, avec accès au détail mission pour
              le suivi des jours et des frais.
            </p>
          </div>
          {!isReadOnly && !showEmptyState ? (
            <Button type="button" onClick={openCreateModal} className="btn-primary">
              <Plus className="h-4 w-4" />
              Créer mission
            </Button>
          ) : null}
        </div>
      </section>

      {error ? <AppErrorState message={error} /> : null}

      {!loading && collaboratorFilter ? (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <p className="text-sm text-slate-300">
            Filtre collaborateur actif:
            {" "}
            <span className="font-semibold text-white">
              {filteredCollaborator?.full_name || collaboratorFilter}
            </span>
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              const nextSearchParams = new URLSearchParams(searchParams);
              nextSearchParams.delete("collaborator");
              setSearchParams(nextSearchParams, { replace: true });
            }}
            className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
          >
            Retirer le filtre
          </Button>
        </section>
      ) : null}

      {!showEmptyState ? (
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(220px,1.5fr)_repeat(4,minmax(150px,1fr))_auto]">
            <Input
              type="search"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({ ...current, search: event.target.value }))
              }
              placeholder="Rechercher une mission, un client, un collaborateur..."
              className="bg-slate-950/80"
            />
            <select
              aria-label="Statut mission"
              value={filters.status}
              onChange={(event) =>
                setFilters((current) => ({ ...current, status: event.target.value }))
              }
              className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
            >
              <option value="all">Toutes</option>
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Client"
              value={filters.clientId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, clientId: event.target.value }))
              }
              className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
            >
              <option value="all">Tous les clients</option>
              {clientOptions.map((client) => (
                <option key={client.value} value={client.value}>
                  {client.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Collaborateur"
              value={filters.collaboratorId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, collaboratorId: event.target.value }))
              }
              className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
            >
              <option value="all">Tous les collaborateurs</option>
              {collaboratorOptions.map((collaborator) => (
                <option key={collaborator.value} value={collaborator.value}>
                  {collaborator.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Période"
              value={filters.period}
              onChange={(event) =>
                setFilters((current) => ({ ...current, period: event.target.value }))
              }
              className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
            >
              {PERIOD_FILTERS.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={resetFilters}
              className="h-9 whitespace-nowrap border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
            >
              Réinitialiser les filtres
            </Button>
          </div>
          <p className="mt-3 text-xs font-medium text-slate-400">
            {filteredMissions.length} missions affichées / {missions.length} au total
          </p>
        </section>
      ) : null}

      {showEmptyState ? (
        <PilotageEmptyState
          icon={BriefcaseBusiness}
          title="Aucune mission"
          description="Créez une mission pour lancer le suivi des placements, jours saisis et marges."
          actionLabel="Créer mission"
          onAction={openCreateModal}
          isReadOnly={isReadOnly}
          testId="missions-empty-state"
        />
      ) : (
        <PilotageTable
          columns={columns}
          rows={filteredMissions}
          loading={loading}
          emptyMessage="Aucune mission ne correspond aux filtres."
          renderActions={(mission) => (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                asChild
                type="button"
                size="sm"
                variant="outline"
                className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
              >
                <Link to={`/admin/pilotage/missions/${mission.id}`}>
                  <Eye className="h-3.5 w-3.5" />
                  Voir détail
                </Link>
              </Button>
              {!isReadOnly ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => openEditModal(mission)}
                    className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Modifier
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(mission)}
                    className="border-red-500/20 text-red-200 hover:bg-red-500/10 hover:text-red-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Supprimer
                  </Button>
                </>
              ) : null}
            </div>
          )}
        />
      )}

      {!isReadOnly ? (
        <PilotageModal
          open={modalOpen}
          onOpenChange={closeModal}
          title={editingMission ? "Modifier la mission" : "Créer une mission"}
          description="Associez un collaborateur à un client pour démarrer le suivi opérationnel."
          onSubmit={handleSubmit}
          submitLabel={editingMission ? "Enregistrer" : "Créer"}
          isSubmitting={saving}
        >
          {formError ? (
            <div
              data-testid="form-error-banner"
              className="mb-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200"
            >
              {formError}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <PilotageField label="Rôle mission" htmlFor="mission-role">
                <Input
                  id="mission-role"
                  placeholder="ex: Développeur KMP, Product Owner..."
                  value={formValues.mission_role}
                  onChange={(event) =>
                    setFormValues((current) => ({
                      ...current,
                      mission_role: event.target.value,
                    }))
                  }
                />
              </PilotageField>
            </div>

            <PilotageField label="Collaborateur" htmlFor="mission-collaborator">
              <select
                id="mission-collaborator"
                value={formValues.collaborator_id}
                onChange={(event) =>
                  setFormValues((current) =>
                    buildFormValuesForCollaborator({
                      ...current,
                      collaborator_id: event.target.value,
                    }, collaboratorById(event.target.value)),
                  )
                }
                className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
                required
              >
                <option value="" disabled>
                  Sélectionner
                </option>
                {collaborators.map((collaborator) => (
                  <option key={collaborator.id} value={collaborator.id}>
                    {collaborator.full_name}
                  </option>
                ))}
              </select>
            </PilotageField>

            <PilotageField label="Client" htmlFor="mission-client">
              <select
                id="mission-client"
                value={formValues.client_id}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    client_id: event.target.value,
                    client_contact_id: "",
                  }))
                }
                className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
                required
              >
                <option value="" disabled>
                  Sélectionner
                </option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </PilotageField>

            <PilotageField label="Contact client" htmlFor="mission-contact-client">
              <select
                id="mission-contact-client"
                value={formValues.client_contact_id || ""}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    client_contact_id: event.target.value,
                  }))
                }
                className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                <option value="">Aucun contact spécifique</option>
                {(clients.find((c) => c.id === formValues.client_id)?.contacts || []).map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.full_name} {contact.role ? `(${contact.role})` : ""}
                  </option>
                ))}
              </select>
            </PilotageField>

            <PilotageField label="Date de début" htmlFor="mission-start-date">
              <Input
                id="mission-start-date"
                type="date"
                value={formValues.start_date}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    start_date: event.target.value,
                  }))
                }
                required
              />
            </PilotageField>

            <PilotageField label="Date de fin" htmlFor="mission-end-date" error={fieldErrors.end_date}>
              <Input
                id="mission-end-date"
                type="date"
                value={formValues.end_date}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    end_date: event.target.value,
                  }))
                }
              />
            </PilotageField>

            <PilotageField label="Statut" htmlFor="mission-status">
              <select
                id="mission-status"
                value={formValues.status}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                }
                className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                {MISSION_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {missionStatusLabel(status)}
                  </option>
                ))}
              </select>
            </PilotageField>

            <PilotageField label="Type" htmlFor="mission-type-freelance">
              <div
                data-testid="mission-type"
                className="grid grid-cols-2 gap-2 rounded-xl border border-white/10 bg-slate-950 p-1"
              >
                {MISSION_TYPES.map((missionType) => {
                  const isActive = effectiveMissionType === missionType;

                  return (
                    <label
                      key={missionType}
                      htmlFor={`mission-type-${missionType.toLowerCase()}`}
                      className={`flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive
                          ? "bg-lenvia-blue text-white"
                          : "text-slate-300"
                        } ${isMissionTypeLocked
                          ? "cursor-not-allowed opacity-70"
                          : "cursor-pointer hover:bg-white/5"
                        }`}
                    >
                      <input
                        id={`mission-type-${missionType.toLowerCase()}`}
                        type="radio"
                        name="mission-type"
                        value={missionType}
                        checked={isActive}
                        onChange={() =>
                          !isMissionTypeLocked &&
                          setFormValues((current) =>
                            normalizeMissionFormForType(current, missionType),
                          )
                        }
                        disabled={isMissionTypeLocked}
                        className="sr-only"
                      />
                      {missionType}
                    </label>
                  );
                })}
              </div>
              {isMissionTypeLocked ? (
                <p className="mt-2 text-xs text-slate-500" data-testid="mission-type-hint">
                  Type hérité du collaborateur
                </p>
              ) : null}
            </PilotageField>

            <PilotageField label="TJM client" htmlFor="mission-tarif-client" error={fieldErrors.tarif_client}>
              <Input
                id="mission-tarif-client"
                type="number"
                min="0"
                step="0.01"
                value={formValues.tarif_client}
                onChange={(event) => {
                  const newTarifVal = event.target.value;
                  setFormValues((current) => {
                    const tarif = parseLocaleNumber(newTarifVal) || 0;
                    if (current.intermediaire_margin_mode === "AMOUNT") {
                      const amtNum = parseLocaleNumber(current.intermediaire_margin_amount) || 0;
                      const pctVal = tarif > 0 ? (amtNum / tarif) * 100 : 0;
                      return {
                        ...current,
                        tarif_client: newTarifVal,
                        intermediaire_margin_pct: String(Math.round(pctVal * 10000) / 10000),
                      };
                    } else {
                      const pctRatio = parsePercentInputToRatio(current.intermediaire_margin_pct) || 0;
                      const amtVal = tarif * pctRatio;
                      return {
                        ...current,
                        tarif_client: newTarifVal,
                        intermediaire_margin_amount: String(Math.round(amtVal * 100) / 100),
                      };
                    }
                  });
                }}
                required
              />
            </PilotageField>

            {effectiveMissionType === "FREELANCE" ? (
              <PilotageField label={CONSULTANT_RATE_LABEL} htmlFor="mission-tjm-consultant" error={fieldErrors.tjm_consultant}>
                <Input
                  id="mission-tjm-consultant"
                  data-testid="mission-tjm-consultant"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formValues.tjm_consultant}
                  onChange={(event) =>
                    setFormValues((current) => ({
                      ...current,
                      tjm_consultant: event.target.value,
                    }))
                  }
                  required
                />
              </PilotageField>
            ) : (
              <>
                {selectedCollaborator?.annual_salary != null ? (
                  <div
                    data-testid="cdi-salary-sync-notice"
                    className="col-span-full rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        data-testid="cdi-salary-source-badge"
                        className="inline-flex items-center rounded-md bg-emerald-400/10 px-2 py-0.5 text-xs font-semibold text-emerald-400 ring-1 ring-inset ring-emerald-400/20"
                      >
                        Données issues de la fiche collaborateur
                      </span>
                      <span className="text-xs text-slate-300">
                        Rémunération centralisée et synchronisée automatiquement.
                      </span>
                    </div>
                    <Link
                      to={`/admin/pilotage/collaborateurs/${selectedCollaborator.id}`}
                      target="_blank"
                      className="text-xs text-sky-400 hover:text-sky-300 hover:underline flex items-center gap-1 self-start sm:self-auto"
                    >
                      Modifier sur la fiche
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                ) : (
                  <div
                    data-testid="cdi-salary-missing-notice"
                    className="col-span-full rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        data-testid="cdi-salary-missing-badge"
                        className="inline-flex items-center rounded-md bg-amber-400/10 px-2 py-0.5 text-xs font-semibold text-amber-400 ring-1 ring-inset ring-amber-400/20"
                      >
                        Rémunération non renseignée sur la fiche
                      </span>
                      <span className="text-xs text-slate-300">
                        Renseignez la rémunération sur la fiche pour activer la synchronisation automatique.
                      </span>
                    </div>
                    {selectedCollaborator ? (
                      <Link
                        to={`/admin/pilotage/collaborateurs/${selectedCollaborator.id}`}
                        target="_blank"
                        className="text-xs text-sky-400 hover:text-sky-300 hover:underline flex items-center gap-1 self-start sm:self-auto"
                      >
                        Ouvrir la fiche
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    ) : null}
                  </div>
                )}

                <PilotageField
                  label={selectedCollaborator?.annual_salary != null ? "Salaire annuel (fiche collaborateur)" : "Salaire annuel"}
                  htmlFor="mission-salary-annual"
                  error={fieldErrors.salaire_annuel}
                >
                  <Input
                    id="mission-salary-annual"
                    data-testid="mission-salary-annual"
                    type="number"
                    min="0"
                    step="0.01"
                    readOnly={Boolean(selectedCollaborator?.annual_salary != null)}
                    className={selectedCollaborator?.annual_salary != null ? "opacity-80 bg-slate-900 cursor-not-allowed" : ""}
                    value={formValues.salaire_annuel}
                    onChange={(event) =>
                      setFormValues((current) => ({
                        ...current,
                        salaire_annuel: event.target.value,
                      }))
                    }
                    required
                  />
                </PilotageField>

                <PilotageField
                  label={selectedCollaborator?.annual_salary != null ? "Coefficient employeur (fiche collaborateur)" : "Coefficient de coût employeur"}
                  htmlFor="mission-employer-cost-coefficient"
                  tooltip="Coefficient appliqué au salaire brut annuel pour estimer le coût employeur chargé utilisé dans le calcul de rentabilité."
                  error={fieldErrors.employer_cost_coefficient}
                >
                  <Input
                    id="mission-employer-cost-coefficient"
                    data-testid="mission-employer-cost-coefficient"
                    type="number"
                    min="0.1"
                    max="10"
                    step="0.01"
                    readOnly={Boolean(selectedCollaborator?.annual_salary != null)}
                    className={selectedCollaborator?.annual_salary != null ? "opacity-80 bg-slate-900 cursor-not-allowed" : ""}
                    value={formValues.employer_cost_coefficient}
                    onChange={(event) =>
                      setFormValues((current) => ({
                        ...current,
                        employer_cost_coefficient: event.target.value,
                      }))
                    }
                    required
                  />
                  <div className="mt-1.5 space-y-0.5 text-xs text-slate-400">
                    <p data-testid="cdi-annual-loaded-cost-preview">
                      Coût annuel chargé estimé :{" "}
                      <span className="font-semibold text-white">
                        {formatEUR0(cdiCostEstimates.annualLoadedCost)}
                      </span>
                    </p>
                    <p data-testid="cdi-daily-cost-preview">
                      Coût journalier estimé :{" "}
                      <span className="font-semibold text-white">
                        {cdiCostEstimates.dailyCost.toLocaleString("fr-FR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        €/j
                      </span>
                    </p>
                  </div>
                </PilotageField>

                <PilotageField label={TACE_LABEL} hint="%" htmlFor="mission-tace">
                  <Input
                    id="mission-tace"
                    data-testid="mission-tace"
                    type="number"
                    min="0"
                    step="0.1"
                    value={formValues.tace}
                    onChange={(event) =>
                      setFormValues((current) => ({
                        ...current,
                        tace: event.target.value,
                      }))
                    }
                  />
                </PilotageField>
              </>
            )}

            <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
              <PilotageField
                label="Marge porteur"
                htmlFor="mission-margin"
              >
                <div className="flex gap-2">
                  <select
                    id="mission-margin-mode"
                    data-testid="mission-margin-mode"
                    aria-label="Mode de marge porteur"
                    value={formValues.intermediaire_margin_mode}
                    onChange={(event) => {
                      const nextMode = event.target.value;
                      setFormValues((current) => {
                        const tarif = parseLocaleNumber(current.tarif_client) || 0;
                        let nextPct = current.intermediaire_margin_pct;
                        let nextAmt = current.intermediaire_margin_amount;
                        if (nextMode === "AMOUNT" && tarif > 0) {
                          const pctRatio = parsePercentInputToRatio(current.intermediaire_margin_pct) || 0;
                          nextAmt = String(Math.round(tarif * pctRatio * 100) / 100);
                        } else if (nextMode === "PERCENT" && tarif > 0) {
                          const amtNum = parseLocaleNumber(current.intermediaire_margin_amount) || 0;
                          nextPct = String(Math.round((amtNum / tarif) * 100 * 10000) / 10000);
                        }
                        return {
                          ...current,
                          intermediaire_margin_mode: nextMode,
                          intermediaire_margin_pct: nextPct,
                          intermediaire_margin_amount: nextAmt,
                        };
                      });
                    }}
                    className="flex h-9 rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue w-28"
                  >
                    <option value="PERCENT">%</option>
                    <option value="AMOUNT">€/jour</option>
                  </select>
                  <Input
                    id="mission-margin"
                    data-testid="mission-intermediaire"
                    type="text"
                    inputMode="decimal"
                    value={formValues.intermediaire_margin_mode === "AMOUNT" ? formValues.intermediaire_margin_amount : formValues.intermediaire_margin_pct}
                    onChange={(event) => {
                      const val = event.target.value;
                      setFormValues((current) => {
                        const tarif = parseLocaleNumber(current.tarif_client) || 0;
                        if (current.intermediaire_margin_mode === "AMOUNT") {
                          const amtNum = parseLocaleNumber(val) || 0;
                          const pctVal = tarif > 0 ? (amtNum / tarif) * 100 : 0;
                          return {
                            ...current,
                            intermediaire_margin_amount: val,
                            intermediaire_margin_pct: String(Math.round(pctVal * 10000) / 10000),
                          };
                        } else {
                          const pctRatio = parsePercentInputToRatio(val) || 0;
                          const amtVal = tarif * pctRatio;
                          return {
                            ...current,
                            intermediaire_margin_pct: val,
                            intermediaire_margin_amount: String(Math.round(amtVal * 100) / 100),
                          };
                        }
                      });
                    }}
                  />
                </div>
              </PilotageField>

              <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4 space-y-2 text-sm text-slate-300 flex flex-col justify-center">
                <div className="flex justify-between">
                  <span>Marge porteur :</span>
                  <span className="font-semibold text-white" data-testid="marge-porteur-val">
                    {displayFinancials.mode === "AMOUNT" ? displayFinancials.amtDisplay : displayFinancials.amtDisplay}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Équivalent :</span>
                  <span className="font-semibold text-white" data-testid="marge-porteur-eq">
                    {displayFinancials.pctDisplay}
                  </span>
                </div>
                <div className="flex justify-between border-t border-white/5 pt-2 font-medium">
                  <span className="text-lenvia-blue">TJM net LENVIA :</span>
                  <span className="text-lenvia-blue font-bold" data-testid="marge-porteur-tjm-net">
                    {displayFinancials.tjmNetDisplay}
                  </span>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 rounded-2xl bg-white/[0.02] border border-white/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">Gratuité client</p>
                  <p className="text-xs text-slate-400">
                    Jours offerts négociés avec le client, consommables librement depuis les timesheets.
                  </p>
                </div>
                {editingMission ? (
                  <div className="text-right text-xs">
                    <span className="text-slate-400">Déjà consommés : </span>
                    <span className="font-semibold text-amber-400">{editingMission.client_free_days_consumed || 0} j</span>
                  </div>
                ) : null}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <PilotageField label="Jours de gratuité négociés" htmlFor="mission-free-days">
                  <Input
                    id="mission-free-days"
                    type="number"
                    min={editingMission ? editingMission.client_free_days_consumed || 0 : 0}
                    step="0.5"
                    value={formValues.client_free_days_granted}
                    onChange={(event) =>
                      setFormValues((current) => ({
                        ...current,
                        client_free_days_granted: event.target.value,
                      }))
                    }
                    placeholder="0"
                  />
                </PilotageField>
                <div className="text-xs text-slate-400 italic">
                  {editingMission ? (
                    <span>
                      Solde actuel restant :{" "}
                      <strong className="text-lenvia-blue">
                        {Math.max(
                          0,
                          (parseLocaleNumber(formValues.client_free_days_granted) || 0) -
                            (editingMission.client_free_days_consumed || 0)
                        )}{" "}
                        j
                      </strong>
                    </span>
                  ) : (
                    "Ces jours pourront être consommés depuis les timesheets de la mission."
                  )}
                </div>
              </div>
            </div>

            {/* Section Évolutions tarifaires et salariales */}
            <div className="md:col-span-2 rounded-2xl bg-white/[0.02] border border-white/5 p-4 space-y-3" data-testid="rate-changes-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setRateChangesExpanded((prev) => !prev)}
                    className="flex items-center gap-2 text-sm font-semibold text-white hover:text-lenvia-blue transition-colors focus:outline-none"
                    data-testid="toggle-rate-changes-btn"
                  >
                    {rateChangesExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <span>Évolutions tarifaires et salariales</span>
                  </button>
                  <span
                    data-testid="rate-changes-count-badge"
                    className="rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-slate-300"
                  >
                    {formValues.rate_changes?.length || 0}
                  </span>
                </div>
                {rateChangesExpanded && !rateChangeForm ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleStartAddRateChange}
                    className="border-white/10 text-xs text-slate-200 hover:bg-white/5 hover:text-white"
                    data-testid="add-rate-change-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Ajouter une évolution
                  </Button>
                ) : null}
              </div>

              {fieldErrors.rate_changes ? (
                <div
                  className="rounded-lg bg-red-500/10 border border-red-500/20 p-2.5 text-xs text-red-200"
                  data-testid="rate-changes-error-banner"
                >
                  {fieldErrors.rate_changes}
                </div>
              ) : null}

              {rateChangesExpanded ? (
                <div className="space-y-3 pt-2">
                  {["CDI", "CDD", "ALTERNANT"].includes(effectiveMissionType) && (
                    <div
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl border border-sky-500/20 bg-sky-500/10 px-3 py-2 text-xs text-sky-200"
                      data-testid="cdi-salary-notice"
                    >
                      <span>
                        Pour un collaborateur salarié (CDI / CDD / Alternant), la rémunération et ses évolutions sont gérées exclusivement depuis la fiche collaborateur. Seules les évolutions de TJM client sont modifiables ici.
                      </span>
                      {formValues.collaborator_id ? (
                        <Link
                          to={`/admin/pilotage/collaborateurs/${formValues.collaborator_id}`}
                          className="inline-flex items-center gap-1 font-semibold text-white underline hover:text-sky-300 shrink-0"
                          data-testid="cdi-collaborator-link"
                        >
                          <span>Fiche collaborateur</span>
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : null}
                    </div>
                  )}

                  {(!formValues.rate_changes || formValues.rate_changes.length === 0) && !rateChangeForm ? (
                    <p className="text-xs text-slate-400 italic" data-testid="no-rate-changes-msg">
                      Aucune évolution tarifaire enregistrée pour cette mission.
                    </p>
                  ) : null}

                  {formValues.rate_changes && formValues.rate_changes.length > 0 ? (
                    <div className="space-y-2">
                      {formValues.rate_changes.map((rc) => {
                        const isActive = rc.effective_date <= todayIso;
                        const fieldLabel = getRateFieldLabel(rc.field);
                        const { prevValue, diffAmount, diffPct } = computeEvolutionDiff(rc, formValues);
                        const currentVal = rc.value !== undefined && rc.value !== null ? rc.value : rc.new_value;
                        return (
                          <div
                            key={rc.id || `${rc.field}-${rc.effective_date}`}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl bg-slate-900/60 border border-white/5 px-3 py-2 text-xs"
                            data-testid={`rate-change-item-${rc.field}-${rc.effective_date}`}
                          >
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-white">
                                  {formatDateFr(rc.effective_date)}
                                </span>
                                <span
                                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                    isActive
                                      ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                                      : "bg-blue-500/15 text-blue-300 border border-blue-500/30"
                                  }`}
                                  data-testid={`rate-change-badge-${rc.field}-${rc.effective_date}`}
                                >
                                  {isActive ? "Active" : "Programmée"}
                                </span>
                                <span className="text-slate-400 font-medium">{fieldLabel}</span>
                              </div>
                              <div className="text-slate-300 flex flex-wrap items-center gap-2">
                                <span>
                                  {formatEUR0(prevValue)} → <strong className="text-white">{formatEUR0(currentVal)}</strong>
                                </span>
                                <span className={diffAmount >= 0 ? "text-emerald-400 font-semibold" : "text-red-400 font-semibold"}>
                                  ({diffAmount >= 0 ? `+${formatEUR0(diffAmount)}` : formatEUR0(diffAmount)}, {diffPct >= 0 ? `+${diffPct.toFixed(1)}%` : `${diffPct.toFixed(1)}%`})
                                </span>
                                {rc.comment ? <span className="text-slate-400 italic">« {rc.comment} »</span> : null}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 self-end sm:self-center">
                              {["annual_salary", "salary"].includes(rc.field) ? (
                                <div className="flex items-center gap-2">
                                  <span
                                    className="rounded px-2 py-0.5 text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30"
                                    data-testid={`rate-change-readonly-${rc.field}-${rc.effective_date}`}
                                  >
                                    Lecture seule (Fiche collaborateur)
                                  </span>
                                  {formValues.collaborator_id ? (
                                    <Link
                                      to={`/admin/pilotage/collaborateurs/${formValues.collaborator_id}`}
                                      className="inline-flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300 hover:underline"
                                      data-testid={`view-collab-from-rc-${rc.field}-${rc.effective_date}`}
                                    >
                                      <span>Voir fiche</span>
                                      <ExternalLink className="h-3 w-3" />
                                    </Link>
                                  ) : null}
                                </div>
                              ) : (
                                <>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleStartEditRateChange(rc)}
                                    className="h-7 px-2 text-slate-300 hover:text-white hover:bg-white/5"
                                    data-testid={`edit-rate-change-${rc.field}-${rc.effective_date}`}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteRateChange(rc)}
                                    className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                    data-testid={`delete-rate-change-${rc.field}-${rc.effective_date}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  {rateChangeForm ? (
                    <div className="rounded-xl border border-lenvia-blue/30 bg-slate-900/90 p-3 space-y-3" data-testid="rate-change-form">
                      <p className="text-xs font-semibold text-lenvia-blue">
                        {rateChangeForm.id ? "Modifier l'évolution tarifaire" : "Nouvelle évolution tarifaire"}
                      </p>

                      {rateChangeForm.isPastDate ? (
                        <div
                          className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 p-2 text-xs text-amber-200"
                          data-testid="past-date-warning"
                        >
                          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
                          <span>
                            Attention : modifier un tarif passé recalculera rétroactivement le CA et la marge des mois concernés.
                          </span>
                        </div>
                      ) : null}

                      {rateChangeSubformError ? (
                        <div
                          className="rounded-lg bg-red-500/10 border border-red-500/20 p-2 text-xs text-red-200"
                          data-testid="rate-change-subform-error"
                        >
                          {rateChangeSubformError}
                        </div>
                      ) : null}

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="text-[11px] text-slate-400 block mb-1">Date d'effet *</label>
                          <Input
                            type="date"
                            value={rateChangeForm.effective_date}
                            onChange={(e) => {
                              const nextDate = e.target.value;
                              setRateChangeForm((curr) => ({
                                ...curr,
                                effective_date: nextDate,
                                isPastDate: Boolean(nextDate && nextDate <= todayIso),
                              }));
                            }}
                            className="h-8 text-xs bg-slate-950"
                            data-testid="rate-change-date-input"
                          />
                        </div>

                        <div>
                          <label className="text-[11px] text-slate-400 block mb-1">Champ concerné *</label>
                          <select
                            value={rateChangeForm.field}
                            onChange={(e) => setRateChangeForm((curr) => ({ ...curr, field: e.target.value }))}
                            className="flex h-8 w-full rounded-md border border-white/10 bg-slate-950 px-2 text-xs text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
                            data-testid="rate-change-field-select"
                          >
                            <option value="tjm">TJM client</option>
                            {effectiveMissionType === "FREELANCE" && (
                              <option value="cjm">CJM consultant</option>
                            )}
                          </select>
                        </div>

                        <div>
                          <label className="text-[11px] text-slate-400 block mb-1">Nouvelle valeur (€) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={rateChangeForm.value !== undefined && rateChangeForm.value !== "" ? rateChangeForm.value : (rateChangeForm.new_value ?? "")}
                            onChange={(e) => {
                              const val = e.target.value;
                              setRateChangeForm((curr) => ({ ...curr, value: val, new_value: val }));
                            }}
                            placeholder="ex: 650"
                            className="h-8 text-xs bg-slate-950"
                            data-testid="rate-change-value-input"
                          />
                        </div>
                      </div>

                      {rateChangeLiveComparison ? (
                        <div
                          className="flex items-center gap-3 text-xs bg-white/[0.02] p-2 rounded-lg border border-white/5"
                          data-testid="rate-change-live-comparison"
                        >
                          <span className="text-slate-400">
                            Base : {formatEUR0(rateChangeLiveComparison.prevValue)}
                          </span>
                          <span className="text-slate-400">→</span>
                          <span className="font-semibold text-white">
                            Nouveau : {formatEUR0(rateChangeLiveComparison.newValue)}
                          </span>
                          <span
                            className={`font-semibold ${
                              rateChangeLiveComparison.diffAmount >= 0 ? "text-emerald-400" : "text-red-400"
                            }`}
                            data-testid="rate-change-diff-preview"
                          >
                            ({rateChangeLiveComparison.diffAmount >= 0 ? `+${formatEUR0(rateChangeLiveComparison.diffAmount)}` : formatEUR0(rateChangeLiveComparison.diffAmount)},{" "}
                            {rateChangeLiveComparison.diffPct >= 0 ? `+${rateChangeLiveComparison.diffPct.toFixed(1)}%` : `${rateChangeLiveComparison.diffPct.toFixed(1)}%`})
                          </span>
                        </div>
                      ) : null}

                      <div>
                        <label className="text-[11px] text-slate-400 block mb-1">Commentaire (optionnel)</label>
                        <Input
                          type="text"
                          value={rateChangeForm.comment}
                          onChange={(e) => setRateChangeForm((curr) => ({ ...curr, comment: e.target.value }))}
                          placeholder="ex: Avenant annuel client"
                          className="h-8 text-xs bg-slate-950"
                          data-testid="rate-change-comment-input"
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          data-testid="cancel-rate-change-btn"
                          onClick={() => {
                            setRateChangeForm(null);
                            setRateChangeSubformError("");
                          }}
                          className="h-8 text-xs text-slate-400 hover:text-white"
                        >
                          Annuler
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveRateChange}
                          className="h-8 text-xs bg-lenvia-blue hover:bg-lenvia-blue/80 text-white"
                          data-testid="save-rate-change-btn"
                        >
                          Valider l'évolution
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            
            <div className="md:col-span-2 grid gap-4 md:grid-cols-2 mt-2 pt-4 border-t border-white/10">
              <div className="md:col-span-2">
                <p className="text-sm font-semibold text-slate-300">Contact & Facturation client</p>
              </div>
              <PilotageField label="Email contact facturation" htmlFor="mission-contact-email">
                <Input
                  id="mission-contact-email"
                  type="email"
                  value={formValues.contact_email}
                  onChange={(event) =>
                    setFormValues((current) => ({
                      ...current,
                      contact_email: event.target.value,
                    }))
                  }
                  placeholder="compta@client.com"
                />
              </PilotageField>
              <PilotageField label="Téléphone contact facturation" htmlFor="mission-contact-phone">
                <Input
                  id="mission-contact-phone"
                  type="tel"
                  value={formValues.contact_phone}
                  onChange={(event) =>
                    setFormValues((current) => ({
                      ...current,
                      contact_phone: event.target.value,
                    }))
                  }
                  placeholder="01 23 45 67 89"
                />
              </PilotageField>
              <PilotageField label="Adresse de facturation / site" htmlFor="mission-address">
                <Input
                  id="mission-address"
                  value={formValues.address}
                  onChange={(event) =>
                    setFormValues((current) => ({
                      ...current,
                      address: event.target.value,
                    }))
                  }
                  placeholder="Lieu d'exécution ou facturation"
                />
              </PilotageField>
            </div>
          </div>
        </PilotageModal>
      ) : null}
    </div>
  );
};

export default MissionsPage;
